"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { ArrowRight, Loader2, PhoneCall, ShieldCheck, X } from "lucide-react";
import { callbackAction } from "@/app/actions/leads";

// Keep the digits and the pretty (555) 123-4567 form in sync. The server only
// cares about the digits, so the display formatting is purely cosmetic.
function toDigits(v: string) {
  return v.replace(/\D/g, "").slice(0, 10);
}
function formatPhone(digits: string) {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function SubmitButton({ ready }: { ready: boolean }) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      style={{
        background: ready ? "var(--color-cl-blue)" : "rgba(255,255,255,.06)",
        color: ready ? "#08090a" : "var(--color-cl-muted)",
        boxShadow: ready ? "0 16px 34px -16px var(--color-cl-blue)" : "none",
      }}
      className="group flex h-[52px] w-full items-center justify-center gap-2.5 rounded-2xl font-display text-[15px] font-bold tracking-tight transition-[filter,transform] hover:brightness-[1.06] active:scale-[.985] disabled:opacity-60"
    >
      {pending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          Request a call
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </>
      )}
    </button>
  );
}

export function ContactWidget() {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [state, formAction] = useActionState(callbackAction, undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  const digits = useMemo(() => toDigits(phone), [phone]);
  const ready = digits.length === 10;

  // Focus the phone field the moment the panel opens (unless we're already done).
  useEffect(() => {
    if (open && !state?.ok) inputRef.current?.focus();
  }, [open, state?.ok]);

  return (
    <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-4 print:hidden">
      {/* Scoped keyframes: uniquely named so they never collide with app-wide CSS. */}
      <style>{`
        @keyframes cw-ring { 0%{transform:scale(1);opacity:.5} 70%,100%{transform:scale(1.9);opacity:0} }
        @keyframes cw-pop { 0%{opacity:0;transform:translateY(14px) scale(.97)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes cw-draw { to{stroke-dashoffset:0} }
        @keyframes cw-halo { 0%{transform:scale(.85);opacity:.35} 100%{transform:scale(1.3);opacity:0} }
      `}</style>

      {open ? (
        <div
          style={{
            background:
              "linear-gradient(160deg,rgba(255,255,255,.16),rgba(255,255,255,.03) 40%,rgba(59,130,246,.28))",
            boxShadow:
              "0 40px 80px -30px rgba(0,0,0,.9),0 0 0 1px rgba(0,0,0,.6)",
            animation: "cw-pop .28s cubic-bezier(.2,.9,.25,1) both",
          }}
          className="w-[calc(100vw-3rem)] max-w-[392px] rounded-[22px] p-px"
        >
          <div
            style={{ background: "linear-gradient(180deg,#101215,#0b0c0e)" }}
            className="overflow-hidden rounded-[21px]"
          >
            {/* Header */}
            <div className="flex items-start gap-3.5 border-b border-white/[.07] px-5 pb-[18px] pt-5">
              <div className="flex flex-1 flex-col gap-1.5">
                <p className="font-display text-[17px] font-bold tracking-tight text-cl-text">
                  Talk to our team
                </p>
                <p className="text-[13.5px] text-cl-muted">
                  Leave your number and we&apos;ll reach out.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="grid h-7 w-7 place-items-center rounded-[9px] text-cl-muted transition-colors hover:bg-white/[.07] hover:text-cl-text"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {state?.ok ? (
              // Done state
              <div
                style={{ animation: "cw-pop .3s cubic-bezier(.2,.9,.25,1) both" }}
                className="flex flex-col items-center gap-3.5 px-6 pb-[30px] pt-[34px]"
              >
                <div className="relative grid h-14 w-14 place-items-center">
                  <span
                    style={{
                      background: "rgba(34,197,94,.18)",
                      animation: "cw-halo 1.8s ease-out infinite",
                    }}
                    className="absolute inset-0 rounded-full"
                  />
                  <span className="absolute inset-0 rounded-full border border-cl-green/35" />
                  <svg width="26" height="20" viewBox="0 0 26 20" fill="none">
                    <path
                      d="M2 10.5l7.5 7L24 2"
                      stroke="var(--color-cl-green)"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray="40"
                      strokeDashoffset="40"
                      style={{ animation: "cw-draw .5s .08s cubic-bezier(.5,0,.2,1) both" }}
                    />
                  </svg>
                </div>
                <p className="font-display text-2xl font-bold tracking-tight text-cl-text">
                  Got it
                </p>
                <p className="text-center text-[14.5px] text-cl-muted">
                  Our team will contact you shortly.
                </p>
                {digits ? (
                  <p className="mt-1.5 rounded-full border border-white/[.08] bg-[#0e1013] px-3.5 py-2 font-mono text-[11.5px] text-cl-muted">
                    +1 {formatPhone(digits)}
                  </p>
                ) : null}
              </div>
            ) : (
              // Form state
              <form action={formAction} className="flex flex-col gap-3.5 p-5">
                <div className="flex flex-col gap-2.5">
                  <label className="font-mono text-[10.5px] uppercase tracking-[.2em] text-cl-muted">
                    Phone number
                  </label>
                  <div
                    style={{
                      borderColor: state?.error
                        ? "rgba(248,113,113,.6)"
                        : ready
                          ? "rgba(59,130,246,.45)"
                          : "rgba(255,255,255,.09)",
                    }}
                    className="flex h-[52px] items-center gap-2.5 rounded-2xl border bg-[#0e1013] px-3.5 transition-colors"
                  >
                    <span className="border-r border-white/[.08] pr-2.5 font-mono text-sm text-cl-muted">
                      +1
                    </span>
                    <input
                      ref={inputRef}
                      type="tel"
                      name="phone"
                      inputMode="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(formatPhone(toDigits(e.target.value)))}
                      placeholder="(555) 123-4567"
                      className="min-w-0 flex-1 bg-transparent font-mono text-base tracking-[.02em] text-cl-text outline-none placeholder:text-cl-muted/40"
                    />
                    <span className="font-mono text-[11px] text-cl-muted/70">
                      {digits.length ? `${digits.length}/10` : ""}
                    </span>
                  </div>
                </div>

                <SubmitButton ready={ready} />

                {state?.error ? (
                  <p className="font-mono text-xs text-red-400">{state.error}</p>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-cl-muted/80">
                    <ShieldCheck className="h-3.5 w-3.5 flex-none" />
                    <span>One call, no marketing. Typical response within 24 hours.</span>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      ) : null}

      {/* Floating action button */}
      <div className="relative h-11 w-11 flex-none">
        {!open ? (
          <span
            style={{
              background: "var(--color-cl-blue)",
              animation: "cw-ring 2.6s cubic-bezier(.3,0,.3,1) infinite",
            }}
            className="pointer-events-none absolute inset-0 rounded-full"
          />
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close contact widget" : "Talk to our team"}
          aria-expanded={open}
          style={{
            background: "var(--color-cl-blue)",
            boxShadow: "0 18px 40px -14px rgba(59,130,246,.75)",
          }}
          className="absolute inset-0 grid place-items-center rounded-full border border-white/[.16] text-[#08090a] transition-transform hover:scale-105 active:scale-95"
        >
          {open ? <X className="h-[17px] w-[17px]" /> : <PhoneCall className="h-[17px] w-[17px]" />}
        </button>
      </div>
    </div>
  );
}
